import { Globe, Menu, X } from "lucide-react";
import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { NUSTARTZ_LOGO } from "../constants/imageConstants";
import Modal from "./common/Modal";
import { SignupForm } from "./common/SignupForm";

interface HeaderProps {
  showOnlyLogo?: boolean;
}

export default function Header({ showOnlyLogo = false }: HeaderProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSignupModalOpen, setIsSignupModalOpen] = useState(false);
  const location = useLocation();

  const isActive = (path: string) => location.pathname === path;

  // If showOnlyLogo is true, render only the logo
  if (showOnlyLogo) {
    return (
      <nav className="relative z-50 px-6 py-8">
        <div className="flex items-center justify-between">
          {/* Logo only */}
          <Link to="/" className="flex-shrink-0">
            <img
              src={NUSTARTZ_LOGO}
              alt="Nustartz Logo"
              className="h-10 w-auto object-contain"
            />
          </Link>
        </div>
      </nav>
    );
  }

  // Full header with all content
  return (
    <nav className="relative z-50 px-6 py-8">
      <div className="flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex-shrink-0">
          <img
            src={NUSTARTZ_LOGO}
            alt="Nustartz Logo"
            className="h-10 w-auto object-contain"
          />
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden lg:flex items-center space-x-8">
          <Link
            to="/"
            className={`text-lg tracking-wide transition-colors relative pb-1 ${
              isActive("/") 
                ? "text-white after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-white after:transition-all" 
                : "text-gray-100 hover:text-white"
            }`}
          >
            Home
          </Link>
          <Link
            to="/services"
            className={`text-lg tracking-wide transition-colors relative pb-1 ${
              isActive("/services")
                ? "text-white after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-white after:transition-all"
                : "text-gray-100 hover:text-white"
            }`}
          >
            Services
          </Link>
          <Link
            to="/insights"
            className={`text-lg tracking-wide transition-colors relative pb-1 ${
              isActive("/insights")
                ? "text-white after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-white after:transition-all"
                : "text-gray-100 hover:text-white"
            }`}
          >
            Insights
          </Link>
          <Link
            to="/ai-agents"
            className={`text-lg tracking-wide transition-colors relative pb-1 ${
              isActive("/ai-agents")
                ? "text-white after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-white after:transition-all"
                : "text-gray-100 hover:text-white"
            }`}
          >
            AI Agents
          </Link>
          <Link
            to="/about-us"
            className={`text-lg tracking-wide transition-colors relative pb-1 ${
              isActive("/about-us")
                ? "text-white after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-white after:transition-all"
                : "text-gray-100 hover:text-white"
            }`}
          >
            About Us
          </Link>
        </div>

        {/* Desktop Right Section */}
        <div className="hidden lg:flex items-center gap-10">
          <Globe
            size={34}
            className="text-white cursor-pointer hover:text-gray-300 transition-colors"
          />
          <button 
            onClick={() => setIsSignupModalOpen(true)}
            className="relative inline-flex h-12 overflow-hidden rounded-xl p-[2px] focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 focus:ring-offset-slate-50"
          >
            <span className="inline-flex h-full w-full cursor-pointer items-center justify-center rounded-xl bg-white px-8 py-3 text-base font-medium text-[#466DEC] backdrop-blur-3xl">
              Start Now
            </span>
          </button>
        </div>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          className="lg:hidden text-white p-2"
        >
          {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="lg:hidden absolute top-full left-0 right-0 bg-[#182746] border-t border-white/20 backdrop-blur-md">
          <div className="px-4 py-6 space-y-4">
            <Link
              to="/"
              className={`block text-lg py-2 tracking-wide transition-colors ${
                isActive("/") ? "text-white" : "text-gray-100"
              }`}
              onClick={() => setIsMenuOpen(false)}
            >
              Home
            </Link>
            <Link
              to="/services"
              className={`block text-lg py-2 tracking-wide transition-colors ${
                isActive("/services") ? "text-white" : "text-gray-100"
              }`}
              onClick={() => setIsMenuOpen(false)}
            >
              Services
            </Link>
            <Link
              to="/insights"
              className={`block text-lg py-2 tracking-wide transition-colors ${
                isActive("/insights") ? "text-white" : "text-gray-100"
              }`}
              onClick={() => setIsMenuOpen(false)}
            >
              Insights
            </Link>
            <Link
              to="/ai-agents"
              className={`block text-lg py-2 tracking-wide transition-colors ${isActive("/ai-agents")
                  ? "text-white"
                  : "text-gray-100"
                }`}
              onClick={() => setIsMenuOpen(false)}
            >
              AI Agents
            </Link>
            <Link
              to="/about-us"
              className={`block text-lg py-2 tracking-wide transition-colors ${
                isActive("/about-us") ? "text-white" : "text-gray-100"
              }`}
              onClick={() => setIsMenuOpen(false)}
            >
              About Us
            </Link>

            <div className="pt-4 border-t border-white/20 flex flex-col space-y-4">
              <div className="flex items-center justify-center">
                <Globe size={20} className="text-white" />
              </div>
              <button 
                onClick={() => setIsSignupModalOpen(true)}
                className="relative inline-flex h-12 overflow-hidden rounded-xl p-[2px] focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 focus:ring-offset-slate-50"
              >
                <span className="inline-flex h-full w-full cursor-pointer items-center justify-center rounded-xl bg-white px-8 py-3 text-base font-medium text-[#466DEC] backdrop-blur-3xl">
                  Start Now
                </span>
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Signup Modal */}
      <Modal
        isOpen={isSignupModalOpen}
        onClose={() => setIsSignupModalOpen(false)}
        title="Contact our Team"
      >
        <SignupForm />
      </Modal>
    </nav>
  );
}
